import { useCallback, useEffect, useState } from "react";

import { ActionBar } from "../../components/ActionBar";
import { Button } from "../../components/Button";
import { Field, FieldGrid } from "../../components/Fields";
import { I } from "../../components/icons";
import { Notice } from "../../components/Notice";
import { NavRow, RowGroup } from "../../components/Rows";
import { Segmented } from "../../components/Segmented";
import { Sheet } from "../../components/Sheet";
import { dayLabel, todayStr } from "../../domain/date";
import type { WaterSettings } from "../../domain/types";
import { clock12, fromMin, isOlder, reminderSlots, toMin, waterSettings, waterTarget } from "../../domain/water";
import { pingRemind } from "../../lib/appBridge";
import { askNotificationPermission, isAndroid, isIOS, isStandalone, notificationPermission } from "../../lib/device";
import { downloadIcs, icsCalendar } from "../../lib/ics";
import { loadPushKey, pushHere, subscribePush, unsubscribePush, type PushHere, type SubscribeResult } from "../../lib/push";
import { useKaya } from "../../store/kayaStore";
import { setWaterSettings } from "../../store/toggles";
import SettingsPage from "./SettingsPage";
import "../../components/Water.css";

const EVERY = [60, 90, 120, 180].map((m) => ({ value: m, label: `${m} min` }));
const SKIP = [30, 45, 60, 90].map((m) => ({ value: m, label: `${m} min` }));

type Open = "window" | "skip" | "ios" | null;

/**
 * You › Reminders › Water (board WaterReminders).
 *
 * The settings, and an honest line about how the reminders arrive on this device: a web app
 * cannot wake itself once closed, so they come from Kaya's server as pushes to the installed app
 * (ADR-044, ADR-045). Which notice shows is decided by what the browser can do and the platform
 * lib/device.ts already reads — never a new user-agent test.
 */
export default function WaterReminders() {
  const { S, update, showToast } = useKaya();
  const [open, setOpen] = useState<Open>(null);
  const ds = todayStr();
  const set = waterSettings(S);
  const older = isOlder(S, ds);
  const slots = reminderSlots(S, ds);
  const ceiling = !!waterTarget(S, ds)?.ceiling;

  const ios = isIOS();
  const android = !ios && isAndroid();

  // The server's key and this device's standing, read before any tap: the tap's first wait has
  // to be the permission request, or iOS refuses it (lib/push.ts).
  const [key, setKey] = useState<string | null>(null);
  const [here, setHere] = useState<PushHere | null>(null);
  const [busy, setBusy] = useState(false);
  const recheck = useCallback(async () => {
    const [k, h] = await Promise.all([loadPushKey(), pushHere()]);
    setKey(k);
    setHere(h);
  }, []);
  useEffect(() => {
    void recheck();
  }, [recheck]);

  /** The slots these settings give, for the counter app's alarms. */
  const slotsWith = (patch: Partial<WaterSettings>) =>
    reminderSlots({ ...S, profile: { ...S.profile, water: { ...set, ...patch } } }, ds);

  const save = (patch: Partial<Omit<WaterSettings, "at">>) => {
    update((d) => setWaterSettings(d, patch));
    // Every change of times is a tap, so it is also the moment to re-arm the phone's alarms.
    if (android && set.remindOn && ("from" in patch || "to" in patch || "everyMin" in patch)) pingRemind(slotsWith(patch), ceiling);
  };

  const subscribe = async (): Promise<SubscribeResult> => {
    if (!key) return "unsupported";
    setBusy(true);
    const r = await subscribePush(key);
    setBusy(false);
    if (r === "denied") showToast("Notifications are blocked for Kaya in this browser's settings");
    else if (r === "failed") showToast("Could not reach Kaya's server — try again in a moment");
    await recheck();
    return r;
  };

  const turn = async (on: boolean) => {
    if (!on) {
      // The alarms first, while the tap is fresh; nothing after it waits on the network.
      if (android) pingRemind(null);
      save({ remindOn: false });
      await unsubscribePush();
      await recheck();
      return;
    }
    save({ remindOn: true });
    const slotsOn = slotsWith({ remindOn: true });
    // With permission already held no prompt is coming, so the alarms can go while the tap is
    // fresh; otherwise after the answer, when the tap may have lapsed (the next change re-sends).
    const had = notificationPermission() === "granted";
    if (android && had) pingRemind(slotsOn, ceiling);
    // No push here (a Safari tab, a server without keys): the permission is still worth asking
    // for, from the tap, because the in-app reminders need it too.
    if ((await subscribe()) === "unsupported" && (await askNotificationPermission()) === "denied")
      showToast("Notifications are blocked for Kaya in this browser's settings");
    if (android && !had && notificationPermission() === "granted") pingRemind(slotsOn, ceiling);
  };

  const calendar = () => {
    try {
      downloadIcs(
        "kaya-water.ics",
        icsCalendar(slots.map((time) => ({ time, title: older ? "Time for a glass of water" : "Kaya: water" })))
      );
    } catch {
      showToast("Could not build the calendar file");
    }
  };

  // The last sentence of the notices depends on this device: said until pushes reach it, and
  // gone once they do. Nothing is said while the answer is still loading.
  const receiving = here?.kind === "on";
  const untilLive =
    !here || receiving
      ? ""
      : here.kind === "unavailable"
        ? " Until that is switched on, a reminder shows only while Kaya is open."
        : " Until this device is set up for them, a reminder shows only while Kaya is open.";

  return (
    <SettingsPage title="Water reminders">
      <RowGroup>
        <NavRow
          kind="toggle"
          icon={I.bell}
          title="Water reminders"
          subtitle={
            ceiling
              ? "Off unless you choose — they only say what you have logged"
              : `Up to ${slots.length} a day, fewer as you log`
          }
          checked={set.remindOn}
          onChange={(v) => void turn(v)}
        />
      </RowGroup>

      <RowGroup heading="When">
        {older ? (
          <NavRow
            kind="value"
            title="Times"
            subtitle="None after 7 pm, so no getting up at night"
            value={slots.map((s) => clock12(s).replace(":00", "")).join(", ")}
          />
        ) : (
          <>
            <NavRow
              title="Between"
              value={`${clock12(set.from)} – ${clock12(set.to)}`}
              onClick={() => setOpen("window")}
            />
            <div className="k-row k-water-every">
              <span className="k-row__title">Every</span>
              <Segmented<number>
                ariaLabel="Remind every"
                value={set.everyMin}
                options={EVERY}
                onChange={(v) => save({ everyMin: v })}
              />
            </div>
          </>
        )}
        <NavRow title="Skip if you logged in the last" value={`${set.skipMin} min`} onClick={() => setOpen("skip")} />
        <NavRow
          kind="toggle"
          title="Stop once the target is met"
          checked={set.stopAtTarget}
          onChange={(v) => save({ stopAtTarget: v })}
        />
        <NavRow
          kind="toggle"
          title="Quiet on untracked, food-off and travel days"
          checked={set.quiet}
          onChange={(v) => save({ quiet: v })}
        />
      </RowGroup>

      <section className="k-group" aria-label="How they arrive">
        <h2 className="k-group__h">How they arrive</h2>
        <div className="k-water-kinds">
          {android ? (
            <Notice icon={I.phone}>
              Reminders come from Kaya&rsquo;s server to the installed app. Each carries a +250 ml button.
              {untilLive}
            </Notice>
          ) : ios ? (
            <Notice
              icon={I.phone}
              secondary={isStandalone() ? undefined : { label: "Add to Home Screen — how", onClick: () => setOpen("ios") }}
            >
              On iPhone, reminders work only once Kaya is added to the Home Screen from Safari (iOS 16.4 or
              later) and you allow notifications from inside it. A Safari tab gets none. Deleting the icon
              ends them.{untilLive}
            </Notice>
          ) : (
            <Notice icon={I.info}>
              {receiving
                ? "In this browser a reminder arrives only while the browser is running."
                : "In this browser a reminder shows only while Kaya is open."}{" "}
              The calendar file below works on any device.
            </Notice>
          )}
        </div>
      </section>

      <RowGroup>
        <DeviceRow here={here} on={set.remindOn} busy={busy} ios={ios} android={android} onSetUp={() => void subscribe()} />
        <NavRow
          kind="action"
          icon={I.calendar}
          title="Calendar file instead"
          subtitle="The same times as daily events (.ics)"
          action={
            <Button variant="ghost" icon={I.dl} onClick={calendar}>
              Add
            </Button>
          }
        />
      </RowGroup>

      {open === "window" ? <WindowSheet from={set.from} to={set.to} onClose={() => setOpen(null)} onSave={(v) => {
        save(v);
        setOpen(null);
      }} /> : null}
      {open === "skip" ? (
        <Sheet title="Skip after a drink" sub="No reminder this soon after the last one you logged" onClose={() => setOpen(null)}>
          <Segmented<number>
            ariaLabel="Skip if you logged in the last"
            value={set.skipMin}
            options={SKIP}
            onChange={(v) => {
              save({ skipMin: v });
              setOpen(null);
            }}
          />
        </Sheet>
      ) : null}
      {open === "ios" ? (
        <Sheet title="Add Kaya to the Home Screen" onClose={() => setOpen(null)}>
          <ol className="k-water-steps">
            <li>Open Kaya in Safari.</li>
            <li>Tap Share, then Add to Home Screen.</li>
            <li>Open Kaya from the new icon, come back here and turn reminders on.</li>
          </ol>
        </Sheet>
      ) : null}
    </SettingsPage>
  );
}

function lastSent(ms: number): string {
  const d = new Date(ms);
  const time = clock12(fromMin(d.getHours() * 60 + d.getMinutes()));
  return todayStr(d) === todayStr() ? `Last one at ${time}` : `Last one ${dayLabel(todayStr(d))}, ${time}`;
}

/**
 * Whether this device receives the pushes, as the browser and the server both see it — the one
 * line the settings need to be honest about how reminders arrive here. A device that is not set
 * up while reminders are on can be set up from the row, which is a tap, as iOS requires.
 */
function DeviceRow({
  here,
  on,
  busy,
  ios,
  android,
  onSetUp,
}: {
  here: PushHere | null;
  on: boolean;
  busy: boolean;
  ios: boolean;
  android: boolean;
  onSetUp: () => void;
}) {
  const title = ios || android ? "On this phone" : "In this browser";
  if (!here) return <NavRow kind="value" icon={I.bell} title={title} value="Checking" />;
  switch (here.kind) {
    case "on":
      return (
        <NavRow
          kind="value"
          icon={I.bell}
          title={title}
          subtitle={here.lastOkAt ? lastSent(here.lastOkAt) : "None sent yet"}
          value="Receiving"
        />
      );
    case "none":
      return on ? (
        <NavRow
          icon={I.bell}
          title={title}
          subtitle={busy ? "Setting up" : "Not receiving them — tap to set up"}
          value="Not set up"
          onClick={busy ? undefined : onSetUp}
        />
      ) : (
        <NavRow kind="value" icon={I.bell} title={title} value="Off" />
      );
    case "blocked":
      return (
        <NavRow
          kind="value"
          icon={I.bell}
          title={title}
          subtitle="Allow notifications for Kaya in the browser's settings"
          value="Blocked"
        />
      );
    case "unavailable":
      return <NavRow kind="value" icon={I.bell} title={title} subtitle="Kaya's server is not sending them yet" value="Not yet" />;
    case "unsupported":
      return (
        <NavRow
          kind="value"
          icon={I.bell}
          title={title}
          subtitle={ios ? "Only from the Home Screen app" : "This browser cannot receive them"}
          value="Not here"
        />
      );
  }
}

function WindowSheet({
  from: from0,
  to: to0,
  onSave,
  onClose,
}: {
  from: string;
  to: string;
  onSave: (v: { from: string; to: string }) => void;
  onClose: () => void;
}) {
  const [from, setFrom] = useState(from0);
  const [to, setTo] = useState(to0);
  const ok = /^\d{2}:\d{2}$/.test(from) && /^\d{2}:\d{2}$/.test(to) && toMin(to) > toMin(from);
  return (
    <Sheet
      title="When to remind"
      onClose={onClose}
      footer={
        <ActionBar
          inSheet
          secondary={{ label: "Cancel", onClick: onClose }}
          primary={{ label: "Save", disabled: !ok, reason: "The end has to be after the start", onClick: () => onSave({ from, to }) }}
        />
      }
    >
      <FieldGrid>
        <Field label="From" type="time" value={from} onChange={setFrom} />
        <Field label="To" type="time" value={to} onChange={setTo} />
      </FieldGrid>
    </Sheet>
  );
}
